import { Fragment, memo, useEffect, useState } from "react";
import { AppUser } from "../../pages/api";
import { useSelector } from "react-redux";
import { TextField } from "@mui/material";
import { getVSLanguageCode, micAdornment } from "../VoiceSearch/utils";
import VoiceSearch from "../VoiceSearch";
import { useStyles } from "./styles";
import { useVoiceRecognition } from "../../hooks/useVoiceRecognition";
import { useClickDoubleClick } from "../../hooks/useClickDoubleClick";
import { getOnlyNumbers } from "../../utils/formatNumber";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store";

export interface IProps {
  id?: string;
  name?: string;
  ref?: any;
  field?: any;
  props?: any;
  label?: any;
  type?: string;
  placeholder?: string | null;
  title?: string | null;
  required?: boolean;
  autoFocus?: boolean;
  error?: boolean;
  helperText?: any;
  InputLabelProps?: any;
  inputProps?: any;
  InputProps?: any;
  multiline?: boolean;
  maxRows?: number;
  value?: string | number | null;
  disabled?: boolean;
  sx?: any;
  className?: string;
  fullWidth?: boolean;
  color?: string;
  hasVS?: boolean;
  onChange?: (e: any) => void;
  onKeyDown?: (e: any) => void;
  onWheel?: (e: any) => void;
  onFocus?: (e: any) => void;
  onBlur?: (e: any) => void;
  onClick?: (e: any) => void;
}

export default memo(
  ({
    id,
    name,
    ref,
    field = {},
    props,
    label = undefined,
    type = "text",
    placeholder = "",
    title,
    required = false,
    autoFocus = false,
    error = false,
    helperText = "",
    InputLabelProps = {},
    inputProps,
    InputProps,
    multiline = false,
    maxRows = 4,
    value = undefined,
    disabled = false,
    sx = {},
    className,
    fullWidth,
    color,
    hasVS = true,
    // hasVS = false,
    onChange = undefined,
    onKeyDown = undefined,
    onWheel = undefined,
    onFocus = undefined,
    onBlur = undefined,
    onClick = undefined,
  }: IProps) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const appUser: AppUser = useSelector((state: RootState) => state.appUser);
    const [openVS, setOpenVS] = useState<boolean>(false);

    const {
      isListening,
      isListeningRef,
      language,
      changeLanguage,
      error: voiceError,
      interimText,
      finalText,
      resetText,
      rippleScale,
      start,
      stop,
      restart,
    } = useVoiceRecognition(getVSLanguageCode(t, appUser?.language));

    useEffect(() => {
      const newLang = getVSLanguageCode(t, appUser?.language);
      if (language !== newLang) {
        changeLanguage(newLang);
      }
    }, [appUser?.language]);

    const { handler } = useClickDoubleClick(start, () => setOpenVS(true));

    useEffect(() => {
      if (interimText) {
        if (onChange && typeof onChange === "function") {
          onChange({
            target: {
              name: name,
              value:
                type === "number" ? getOnlyNumbers(interimText) : interimText,
            },
          });
        } else if (props) {
          props.setFieldValue(
            name,
            type === "number" ? getOnlyNumbers(interimText) : interimText,
          );
        }
      }
    }, [interimText]);

    // const data = ["Apple", "Banana", "Orange", "Mango", "Grapes", "Watermelon"];

    // const getSuggestions = (query: string) => {
    //   if (!query) return [];
    //   return data.filter((item) =>
    //     item.toLowerCase().includes(query.toLowerCase())
    //   );
    // };

    // const handleSearch = (query: string) => {
    //   console.log("Searching for:", query);
    // };

    return (
      <Fragment>
        <TextField
          {...field}
          name={name}
          label={label}
          type={type}
          placeholder={placeholder}
          title={title}
          required={required}
          autoFocus={autoFocus}
          size="small"
          error={error}
          helperText={helperText}
          multiline={multiline}
          maxRows={maxRows}
          disabled={disabled}
          onKeyDown={onKeyDown}
          onWheel={onWheel}
          onFocus={onFocus}
          onBlur={onBlur}
          onClick={onClick}
          InputLabelProps={{ shrink: true, ...InputLabelProps }}
          inputProps={inputProps}
          InputProps={
            InputProps
              ? hasVS &&
                appUser.user?.currentClient?.hasVS &&
                !disabled &&
                type !== "date" &&
                type !== "time" &&
                type !== "password" &&
                ((onChange && typeof onChange === "function") || props)
                ? ({
                    ...InputProps,
                    endAdornment: (
                      <>
                        {micAdornment(
                          handler,
                          null,
                          isListening || isListeningRef.current,
                          stop,
                          rippleScale,
                        )}
                        {InputProps?.endAdornment}
                      </>
                    ),
                  } as any)
                : InputProps
              : hasVS &&
                  appUser.user?.currentClient?.hasVS &&
                  !disabled &&
                  type !== "date" &&
                  type !== "time" &&
                  type !== "password" &&
                  ((onChange && typeof onChange === "function") || props)
                ? {
                    endAdornment: micAdornment(
                      handler,
                      null,
                      isListening || isListeningRef.current,
                      stop,
                      rippleScale,
                    ),
                  }
                : null
          }
          value={value ?? field?.value ?? ""}
          // {...(value ? { value: value } : {})}
          {...(onChange ? { onChange: onChange } : {})}
          {...(ref ? { ref: ref } : {})}
          sx={sx}
          className={`${classes.input} ${className}`}
          fullWidth={fullWidth}
          color={color}
        />
        {hasVS && appUser.user?.currentClient?.hasVS && openVS && (
          <VoiceSearch
            label={label}
            onClose={() => setOpenVS(false)}
            onOk={(text: string) => {
              if (onChange && typeof onChange === "function") {
                onChange({
                  target: {
                    name: name,
                    value: type === "number" ? getOnlyNumbers(text) : text,
                  },
                });
              } else if (props) {
                props.setFieldValue(
                  name,
                  type === "number" ? getOnlyNumbers(text) : text,
                );
              }
              setOpenVS(false);
            }}
            // onSearch={handleSearch}
            // getSuggestions={getSuggestions}
          />
        )}
      </Fragment>
    );
  },
);
